import type { Permission } from './types';
import { clamp, safeNumber, safeString } from './math';
import { isValidLatLng } from './geo';

/* ------------------------------------------------------------------ *
 * 指令目录
 * ------------------------------------------------------------------ */

export type DroneCommandType =
  // 飞行控制
  | 'arm'
  | 'disarm'
  | 'takeoff'
  | 'hover'
  | 'land'
  | 'rtl'
  | 'emergency-stop'
  // 航线任务
  | 'mission-upload'
  | 'mission-start'
  | 'mission-pause'
  | 'mission-resume'
  // 维护
  | 'reboot-flight-controller'
  | 'reboot-link'
  | 'calibrate-attitude'
  | 'calibrate-compass';

export type CommandGroup = 'flight' | 'mission' | 'maintenance';

/**
 * 指令在界面上的强调色。
 *
 * - `default`：普通指令，中性样式；
 * - `danger`：危险指令，红色告警样式；
 * - `accent`：**危险但高频**的指令（起飞、降落）—— 仍然必须二次确认，
 *   但用蓝色强调而不是红色告警，避免把日常操作渲染成刺眼的红色，
 *   同时把文案改成「检查…条件」的现场确认口径。
 */
export type CommandEmphasis = 'danger' | 'accent';
export type CommandTone = 'default' | CommandEmphasis;

export interface CommandParamSpec {
  name: string;
  label: string;
  type: 'number' | 'text';
  required: boolean;
  unit?: string;
  min?: number;
  max?: number;
  defaultValue?: number | string;
  placeholder?: string;
  help?: string;
}

export interface DroneCommandMeta {
  type: DroneCommandType;
  label: string;
  group: CommandGroup;
  /** 下发该指令所需的权限位 */
  permission: Permission;
  /** 危险操作：前端使用更醒目的警示样式，并在确认弹窗中要求二次确认 */
  destructive: boolean;
  /**
   * 界面强调色。缺省由 `destructive` 推导（危险 → `danger`）。
   * 危险但高频的指令（起飞、降落）显式声明为 `accent`，用蓝色强调替代红色告警。
   */
  emphasis?: CommandEmphasis;
  /** 指令面板上的按钮文案；缺省用 `label`。起飞/降落用它把按钮改成「检查…条件」。 */
  buttonLabel?: string;
  /** 下发前必须逐条确认的现场条件。声明后，确认界面会以检查清单呈现。 */
  preflight?: readonly string[];
  description: string;
  params: readonly CommandParamSpec[];
}

/** 该指令在界面上应使用的强调色。非危险指令一律中性。 */
export function commandTone(meta: DroneCommandMeta): CommandTone {
  if (!meta.destructive) return 'default';
  return meta.emphasis ?? 'danger';
}

/** 指令面板上的按钮文案。 */
export function commandButtonLabel(meta: DroneCommandMeta): string {
  return meta.buttonLabel ?? meta.label;
}

/** 二次确认界面的标题：条件检查类指令用「检查…条件」的措辞，其余指令用「…确认」。 */
export function commandConfirmTitle(meta: DroneCommandMeta): string {
  return meta.buttonLabel ?? `${meta.label}确认`;
}

/** 单次上传航线的航点上限。 */
export const MISSION_WAYPOINT_LIMIT = 200;
export const TAKEOFF_ALT_MIN_M = 1;
export const TAKEOFF_ALT_MAX_M = 500;

export const COMMAND_GROUP_LABELS: Readonly<Record<CommandGroup, string>> = {
  flight: '飞行控制',
  mission: '航线任务',
  maintenance: '设备维护',
};

export const DRONE_COMMANDS: Readonly<Record<DroneCommandType, DroneCommandMeta>> = {
  arm: {
    type: 'arm',
    label: '解锁电机',
    group: 'flight',
    permission: 'drone.command.flight',
    destructive: true,
    description: '解锁电机。解锁后螺旋桨将可转动，请确保飞行器周围无人。',
    params: [],
  },
  disarm: {
    type: 'disarm',
    label: '上锁电机',
    group: 'flight',
    permission: 'drone.command.flight',
    destructive: false,
    description: '锁定电机，禁止转动。',
    params: [],
  },
  takeoff: {
    type: 'takeoff',
    label: '起飞',
    // 起飞危险但高频：蓝色强调 + 现场条件检查，不作为红色告警操作处理
    emphasis: 'accent',
    buttonLabel: '检查起飞条件',
    preflight: [
      '起降场地已净空，人员与车辆撤离至安全距离外',
      '机臂已展开到位、桨叶已锁紧，电池卡扣到位',
      '电量、卫星数与链路均正常（电量 ≥ 30%，卫星 ≥ 8 颗）',
    ],
    group: 'flight',
    permission: 'drone.command.flight',
    destructive: true,
    description: '自动起飞并爬升至指定高度后悬停。',
    params: [
      {
        name: 'altitudeM',
        label: '目标高度',
        type: 'number',
        required: true,
        unit: 'm',
        min: TAKEOFF_ALT_MIN_M,
        max: TAKEOFF_ALT_MAX_M,
        defaultValue: 15,
        help: `取值 ${TAKEOFF_ALT_MIN_M}–${TAKEOFF_ALT_MAX_M} 米`,
      },
    ],
  },
  hover: {
    type: 'hover',
    label: '悬停',
    group: 'flight',
    permission: 'drone.command.flight',
    destructive: false,
    description: '在当前坐标保持悬停。',
    params: [],
  },
  land: {
    type: 'land',
    label: '降落',
    // 与起飞同理：危险但常用，蓝色强调 + 场地条件检查
    emphasis: 'accent',
    buttonLabel: '检查降落条件',
    preflight: [
      '降点下方无人、无车辆与障碍物，地面平整',
      '降落全程保持目视可见，随时可以接管',
      '确认空域内无其他飞行器与本机高度冲突',
    ],
    group: 'flight',
    permission: 'drone.command.flight',
    destructive: true,
    description: '在当前坐标垂直降落。请确认下方区域安全。',
    params: [],
  },
  rtl: {
    type: 'rtl',
    label: '返航',
    group: 'flight',
    permission: 'drone.command.flight',
    destructive: false,
    description: '按预设返航高度飞回起飞点并降落。',
    params: [],
  },
  'emergency-stop': {
    type: 'emergency-stop',
    label: '紧急停止',
    group: 'flight',
    permission: 'drone.command.flight',
    destructive: true,
    description: '立即切断动力。飞行器将直接坠落，仅在人员或财产面临即刻危险时使用。',
    params: [],
  },
  'mission-upload': {
    type: 'mission-upload',
    label: '上传航线',
    group: 'mission',
    permission: 'drone.command.mission',
    destructive: false,
    description: '上传航点序列到飞行器。建议在地面完成上传。',
    params: [
      {
        name: 'waypoints',
        label: '航点',
        type: 'text',
        required: true,
        placeholder: '每行一个航点：纬度,经度,高度(m),速度(m/s)',
        help: `最多 ${MISSION_WAYPOINT_LIMIT} 个航点，按输入顺序连接`,
      },
    ],
  },
  'mission-start': {
    type: 'mission-start',
    label: '执行航线',
    group: 'mission',
    permission: 'drone.command.mission',
    destructive: true,
    description: '开始执行已上传的航线任务。',
    params: [],
  },
  'mission-pause': {
    type: 'mission-pause',
    label: '暂停航线',
    group: 'mission',
    permission: 'drone.command.mission',
    destructive: false,
    description: '暂停航线执行，飞行器将在当前航段悬停。',
    params: [],
  },
  'mission-resume': {
    type: 'mission-resume',
    label: '继续航线',
    group: 'mission',
    permission: 'drone.command.mission',
    destructive: false,
    description: '从暂停的航点继续执行航线。',
    params: [],
  },
  'reboot-flight-controller': {
    type: 'reboot-flight-controller',
    label: '重启飞控',
    group: 'maintenance',
    permission: 'drone.command.maintenance',
    destructive: true,
    description: '重启飞行控制器。飞行中严禁执行。',
    params: [],
  },
  'reboot-link': {
    type: 'reboot-link',
    label: '重启链路',
    group: 'maintenance',
    permission: 'drone.command.maintenance',
    destructive: true,
    description: '重启图传/数传模块，期间将短暂失联。',
    params: [],
  },
  'calibrate-attitude': {
    type: 'calibrate-attitude',
    label: '姿态校准',
    group: 'maintenance',
    permission: 'drone.command.maintenance',
    destructive: false,
    description: '执行水平姿态校准，需将飞行器置于水平静止状态。',
    params: [],
  },
  'calibrate-compass': {
    type: 'calibrate-compass',
    label: '罗盘校准',
    group: 'maintenance',
    permission: 'drone.command.maintenance',
    destructive: false,
    description: '执行罗盘校准，需按提示旋转飞行器。',
    params: [],
  },
};

export const COMMAND_ORDER: readonly DroneCommandType[] = [
  'takeoff',
  'hover',
  'rtl',
  'land',
  'arm',
  'disarm',
  'emergency-stop',
  'mission-upload',
  'mission-start',
  'mission-pause',
  'mission-resume',
  'reboot-flight-controller',
  'reboot-link',
  'calibrate-attitude',
  'calibrate-compass',
];

export function isDroneCommandType(value: unknown): value is DroneCommandType {
  return typeof value === 'string' && value in DRONE_COMMANDS;
}

/* ------------------------------------------------------------------ *
 * 可用性判定
 * ------------------------------------------------------------------ */

export interface CommandAvailabilityContext {
  armed: boolean;
  inAir: boolean;
  missionState: 'idle' | 'uploading' | 'ready' | 'executing' | 'paused' | 'completed' | 'failed';
  missionTotalWaypoints: number;
}

export interface CommandAvailability {
  enabled: boolean;
  /** 禁用原因，直接展示给操作员 */
  reason: string | null;
}

/**
 * 判断某条指令在当前飞行状态下是否可下发。
 * 前端据此禁用按钮，Worker 与无人机侧仍会各自再校验一次。
 */
export function commandAvailability(
  type: DroneCommandType,
  ctx: CommandAvailabilityContext,
): CommandAvailability {
  const grounded = !ctx.inAir;
  const busy = ctx.missionState === 'executing';

  switch (type) {
    case 'arm':
      if (ctx.armed) return { enabled: false, reason: '电机已处于解锁状态' };
      if (ctx.inAir) return { enabled: false, reason: '飞行中不可解锁电机' };
      return { enabled: true, reason: null };

    case 'disarm':
      if (!ctx.armed) return { enabled: false, reason: '电机已处于上锁状态' };
      return { enabled: true, reason: null };

    case 'takeoff':
      if (ctx.inAir) return { enabled: false, reason: '飞行器已在空中' };
      return { enabled: true, reason: null };

    case 'hover':
    case 'rtl':
      if (grounded) return { enabled: false, reason: '飞行器未起飞' };
      return { enabled: true, reason: null };

    case 'land':
      if (grounded) return { enabled: false, reason: '飞行器已在地面' };
      return { enabled: true, reason: null };

    case 'emergency-stop':
      if (grounded && !ctx.armed) return { enabled: false, reason: '飞行器未上电' };
      return { enabled: true, reason: null };

    case 'mission-upload':
      if (ctx.inAir) return { enabled: false, reason: '建议在地面完成航线上传' };
      return { enabled: true, reason: null };

    case 'mission-start':
      if (busy) return { enabled: false, reason: '航线正在执行中' };
      if (ctx.missionState === 'paused') return { enabled: false, reason: '请使用「继续航线」' };
      if (ctx.missionTotalWaypoints <= 0) return { enabled: false, reason: '尚未上传航线' };
      return { enabled: true, reason: null };

    case 'mission-pause':
      if (!busy) return { enabled: false, reason: '当前没有正在执行的航线' };
      return { enabled: true, reason: null };

    case 'mission-resume':
      if (ctx.missionState !== 'paused') return { enabled: false, reason: '航线未处于暂停状态' };
      return { enabled: true, reason: null };

    default:
      // 维护类指令一律要求落地
      if (!grounded) return { enabled: false, reason: '维护类指令仅可在落地后执行' };
      return { enabled: true, reason: null };
  }
}

/* ------------------------------------------------------------------ *
 * 载荷构造与校验
 * ------------------------------------------------------------------ */

export interface CommandFieldError {
  field: string;
  message: string;
}

export type BuildCommandResult =
  | { ok: true; payload: Record<string, unknown> }
  | { ok: false; errors: CommandFieldError[] };

/** 解析「纬度,经度,高度,速度」逐行文本。 */
export function parseWaypointText(
  text: string,
): { ok: true; waypoints: { lat: number; lng: number; altM: number; speedMs: number }[] } | { ok: false; errors: CommandFieldError[] } {
  const errors: CommandFieldError[] = [];
  const waypoints: { lat: number; lng: number; altM: number; speedMs: number }[] = [];
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');

  if (lines.length === 0) {
    return { ok: false, errors: [{ field: 'waypoints', message: '请至少输入一个航点' }] };
  }
  if (lines.length > MISSION_WAYPOINT_LIMIT) {
    return {
      ok: false,
      errors: [{ field: 'waypoints', message: `航点数量超过上限（${MISSION_WAYPOINT_LIMIT}）` }],
    };
  }

  lines.forEach((line, index) => {
    const parts = line.split(',').map((part) => part.trim());
    const lineNo = index + 1;
    if (parts.length < 3) {
      errors.push({ field: 'waypoints', message: `第 ${lineNo} 行格式错误，至少需要「纬度,经度,高度」` });
      return;
    }
    const lat = safeNumber(parts[0], Number.NaN);
    const lng = safeNumber(parts[1], Number.NaN);
    const altM = safeNumber(parts[2], Number.NaN);
    const speedMs = parts[3] === undefined ? 5 : safeNumber(parts[3], Number.NaN);

    if (!isValidLatLng(lat, lng)) {
      errors.push({ field: 'waypoints', message: `第 ${lineNo} 行的经纬度不合法` });
      return;
    }
    if (!Number.isFinite(altM) || altM < 0 || altM > 500) {
      errors.push({ field: 'waypoints', message: `第 ${lineNo} 行的高度需在 0–500 米之间` });
      return;
    }
    if (!Number.isFinite(speedMs) || speedMs <= 0 || speedMs > 30) {
      errors.push({ field: 'waypoints', message: `第 ${lineNo} 行的速度需在 0–30 米/秒之间` });
      return;
    }
    waypoints.push({ lat, lng, altM, speedMs });
  });

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, waypoints };
}

/**
 * 校验并构造指令载荷。以指令定义中的 params 为唯一依据，
 * 保证前端表单与 Worker 侧校验使用同一套规则。
 */
export function buildCommandPayload(
  type: DroneCommandType,
  params: Record<string, unknown> = {},
): BuildCommandResult {
  const meta = DRONE_COMMANDS[type];
  const errors: CommandFieldError[] = [];
  const payload: Record<string, unknown> = { command: type };

  for (const spec of meta.params) {
    const raw = params[spec.name];

    if (spec.type === 'number') {
      if (raw === undefined || raw === null || raw === '') {
        if (spec.required) errors.push({ field: spec.name, message: `${spec.label}不能为空` });
        else if (spec.defaultValue !== undefined) payload[spec.name] = spec.defaultValue;
        continue;
      }
      const value = safeNumber(raw, Number.NaN);
      if (!Number.isFinite(value)) {
        errors.push({ field: spec.name, message: `${spec.label}必须是数字` });
        continue;
      }
      if (spec.min !== undefined && value < spec.min) {
        errors.push({ field: spec.name, message: `${spec.label}不得小于 ${spec.min}${spec.unit ?? ''}` });
        continue;
      }
      if (spec.max !== undefined && value > spec.max) {
        errors.push({ field: spec.name, message: `${spec.label}不得大于 ${spec.max}${spec.unit ?? ''}` });
        continue;
      }
      payload[spec.name] = clamp(value, spec.min ?? value, spec.max ?? value);
      continue;
    }

    const text = safeString(raw, '');
    if (text.trim() === '') {
      if (spec.required) errors.push({ field: spec.name, message: `${spec.label}不能为空` });
      continue;
    }
    if (spec.name === 'waypoints') {
      const parsed = parseWaypointText(text);
      if (!parsed.ok) {
        errors.push(...parsed.errors);
        continue;
      }
      payload.waypoints = parsed.waypoints;
      continue;
    }
    payload[spec.name] = text;
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, payload };
}

/** 指令下发目标，用于审计与弹窗文案。 */
export interface DroneCommandRequest {
  droneId: string;
  command: DroneCommandType;
  payload: Record<string, unknown>;
}
